/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import cx from 'classnames';
import type { DateValue } from '@internationalized/date';
import { Icon, useFormatDateRange } from 'sarsaparilla';

import styles from './CardAvailabilityBadge.module.scss';

const availabilityOptionsMap = {
    available: {
        label: 'Available',
        color: 'default',
    },
    partiallyAvailable: {
        label: 'Partially Available',
        color: 'default',
    },
    unavailable: {
        label: 'Unavailable',
        color: 'danger',
    },
    lottery: {
        label: 'Lottery',
        color: 'default',
    },
    enterDates: {
        label: 'Enter Dates for Availability',
        color: 'default',
    },
    firstCome: {
        label: 'First-come, First-served',
        color: 'default',
    },

    // The statuses are not used by search, but might be useful for other services so leaving them here.
    new: {
        label: 'New',
        color: 'success',
    },
    enterDate: {
        label: 'Enter Date for Availability',
        color: 'default',
    },
    viewAvailability: {
        label: 'View Availability',
        color: 'default',
    },
    registrationClosed: {
        label: 'Registration Closed',
        color: 'danger',
    },
    registrationOpen: {
        label: 'Registration Open',
        color: 'success',
    },
    registrationComingSoon: {
        label: 'Registration Coming Soon',
        color: 'default',
    },
    waitlistOnly: {
        label: 'Waitlist Only',
        color: 'warning',
    },
};

export type CardAvailability = keyof typeof availabilityOptionsMap;

const dateRangeFormatOptions: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
};

type PresetOption = {
    availability: CardAvailability | null | undefined;
};

type DateRangeOption = {
    dateRange: {
        start: DateValue;
        end: DateValue;
    };
};

export type CardAvailabilityBadgeProps = React.HTMLAttributes<HTMLSpanElement> &
    (PresetOption | DateRangeOption);

/**
 * Badge over the card image showing either an availability status or the
 * date range the item is available for
 */
export function CardAvailabilityBadge(props: CardAvailabilityBadgeProps) {
    const formatDateRange = useFormatDateRange();

    if ('dateRange' in props) {
        const { dateRange, className, ...rest } = props;

        return (
            <span
                className={cx(styles.availabilityBadge, styles.default, className)}
                {...rest}
            >
                <span className={styles.iconWrapper}>
                    <Icon iconName="calendar" className={styles.icon} />
                </span>
                <span className={styles.content}>
                    <span className="rec-sr-only">Available </span>
                    {formatDateRange(
                        dateRange.start,
                        dateRange.end,
                        dateRangeFormatOptions
                    )}
                </span>
            </span>
        );
    }

    const { availability, className, ...rest } = props;

    if (!availability) {
        return null;
    }

    const option = availabilityOptionsMap[availability];

    return (
        <span
            className={cx(styles.availabilityBadge, styles[option.color], className)}
            {...rest}
        >
            <span className={styles.content}>{option.label}</span>
        </span>
    );
}
